<template>
  <InfusePageLayout>
    <template #top-bar>
      <NavigationTopBar />
    </template>

    <div class="flex flex-row gap-4">
      <div class="flex h-16 w-16 items-center justify-center rounded-md border border-neutral-400">
        <font-awesome-icon class="text-4xl" :icon="['fal', 'cube']" />
      </div>
      <div>
        <h2 class="font-bold">
          {{ organisation?.name }}
        </h2>
        <p class="text-sm">
          {{ organisation?.description }}
        </p>
      </div>

      <div class="ml-auto">
        <InfuseButton
          v-if="organisation?.active"
          color="alter"
          @click="onUpdateActive(false)"
          :disabled="isLoading"
          v-permission="{ ids: [organisationPermissions.DEACTIVATE, tenantOrganisationPermissions.DEACTIVATE] }"
        >
          {{ t('iam.organisations.header.deactivate_organisation') }}
        </InfuseButton>
        <InfuseButton
          v-else
          variant="primary-border"
          @click="onUpdateActive(true)"
          :disabled="isLoading"
          v-permission="{ ids: [organisationPermissions.ACTIVATE, tenantOrganisationPermissions.ACTIVATE] }"
        >
          {{ t('iam.organisations.header.activate_organisation') }}
        </InfuseButton>
      </div>
    </div>

    <div class="flex flex-col gap-6 overflow-y-auto">
      <InfuseRouterTabLayout :model="organisation" />
    </div>
  </InfusePageLayout>

  <InfuseConfirmModal
    v-if="organisation && isActiveModalOpen"
    :title="t('iam.organisations.header.activate_organisation')"
    :message="t('iam.organisations.info.activate_organisation')"
    verification-text="activate"
    :loading="isActivatePending"
    @confirm="onActivateOrganisation"
    @close="isActiveModalOpen = false"
  />

  <InfuseConfirmModal
    v-if="organisation && isDeactiveModalOpen"
    :title="t('iam.organisations.header.deactivate_organisation')"
    :message="t('iam.organisations.info.deactivate_organisation')"
    verification-text="deactivate"
    :loading="isDeactivatePending"
    @confirm="onDeactivateOrganisation"
    @close="isDeactiveModalOpen = false"
  />
</template>

<script setup lang="ts">
import { useOrganisation } from '@infuse/iam/organisation/queries';
import { InfusePageLayout, InfuseRouterTabLayout, NavigationTopBar } from '@infuse/ui/layouts';
import { ref } from 'vue';
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useActivateOrganisation, useDeactivateOrganisation } from '@infuse/iam/organisation/mutations';
import { computed } from 'vue';
import { InfuseButton, InfuseConfirmModal } from '@infuse/ui/components';
import { vPermission } from '@infuse/iam/permission/directives';
import { organisationPermissions } from '@infuse/api/iam/organisation';
import { tenantOrganisationPermissions } from '@infuse/api/iam/tenant';

const { t } = useI18n();
const route = useRoute();

const { mutateAsync: activateOrganisation, isPending: isActivatePending } = useActivateOrganisation();
const { mutateAsync: deactivateOrganisation, isPending: isDeactivatePending } = useDeactivateOrganisation();
const { organisation, fetchOrganisationId, isLoading: isOrganisationLoading } = useOrganisation();

const isLoading = computed(() => isOrganisationLoading.value || isActivatePending.value || isDeactivatePending.value);

const isActiveModalOpen = ref(false);
const isDeactiveModalOpen = ref(false);

watch(
  route,
  () => {
    fetchOrganisationId.value = route.params.id as string;
  },
  { immediate: true }
);
function onUpdateActive(value: boolean) {
  if (!organisation.value?.id) {
    return;
  }

  if (value) {
    isActiveModalOpen.value = true;
    isDeactiveModalOpen.value = false;
  } else {
    isActiveModalOpen.value = false;
    isDeactiveModalOpen.value = true;
  }
}

async function onActivateOrganisation() {
  if (!organisation.value?.id) {
    return;
  }

  await activateOrganisation(organisation.value.id);
  isActiveModalOpen.value = false;
}

async function onDeactivateOrganisation() {
  if (!organisation.value?.id) {
    return;
  }

  await deactivateOrganisation(organisation.value?.id);
  isDeactiveModalOpen.value = false;
}
</script>
